import {
  ChatMessage,
  Conversation,
  ConversationHistory,
  ConversationRequest,
  FrontEndSettings,
  ChatResponse,
  CheckAuthResponse,
  Feedback,
  ConversationRating,
  ConversationAttachment,
  ExportRequest,
  ExportAllRequest,
  ExportResponse
} from "./models";
import type { PromptTemplateVersion } from '../models/PromptTemplate';

export async function callConversationApiStreamed(
  request: ConversationRequest,
  abortSignal: AbortSignal,
  onMessage?: (message: ChatResponse) => void
): Promise<Response> {

  // Track the streaming session id (sent as first SSE message)
  let streamId: string | null = null;
  let abortRequested = false;

  const sendCancel = () => {
    if (streamId) {
      // Fire and forget cancellation request to backend
      fetch("/api/cancel-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ streamId: streamId })
      }).catch(err => console.warn("Failed to send cancel-stream request", err));
    }
  };

  const onStreamInfoReceived = (id: string) => {
    streamId = id;
    if (abortRequested) {
      // If abort already happened before we got the id, send cancel now
      sendCancel();
    }
  };

  abortSignal.addEventListener("abort", () => {
    abortRequested = true;
    sendCancel();
  }, { once: true });

  return new Promise((resolve, reject) => {
    fetch("/api/conversation-stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        "Cache-Control": "no-cache",
      },
      body: JSON.stringify({
        conversation_id: request.conversation_id,
        message: request.message,
        is_proposed_answer: request.is_proposed_answer,
        requested_model: request.requested_model,
        temperature: request.temperature,
        response_mode: request.response_mode,
        preferred_index_id: request.preferred_index_id,
        is_retry: request.is_retry,
        prompt_key: request.prompt_key,
      }),
      signal: abortSignal      
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(JSON.stringify(errorData.error));
        }
        if (!response.body) {
          throw new Error("No response body available");
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        // Buffer for incomplete lines
        let buffer = '';
        
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              // Process any remaining buffer content
              if (buffer.trim()) {
                processLines([buffer], onStreamInfoReceived, onMessage);                
              }
              break;
            }
            
            const chunk = decoder.decode(value, { stream: true });
            //console.log("CHUNK: " + chunk);
            
            // Add chunk to buffer
            buffer += chunk;
            
            // Split by newlines but keep the last incomplete line in buffer
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep the last (potentially incomplete) line
            
            // Process complete lines
            const isDone = processLines(lines, onStreamInfoReceived, onMessage);            
            if (isDone) {
              resolve(response);
              return;
            }
          }
          resolve(response);
        } catch (error) {
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function processLines(
  lines: string[], 
  onStreamInfoReceived: (streamInfo: string) => void, 
  onMessage?: (message: ChatResponse) => void) : boolean {

  for (const line of lines) {
    if (line.startsWith('data: ')) {
      const data = line.slice(6).trim();
      if (data === '[DONE]') {
        return true; // Signal completion
      }
      try {
        const messageData = JSON.parse(data);

        if (messageData.streamId) {
          console.log(messageData);
          onStreamInfoReceived(messageData.streamId);
        }
        else {
          if (messageData.error) {
            const errorMessage: ChatResponse = {
              conversationId: messageData.conversationId,
              date: messageData.date,
              title: messageData.title,
              message: messageData.message,
              error: messageData.error
            };

            if (onMessage) {
              onMessage(errorMessage);
            }
          } else if (onMessage && messageData) {
            onMessage(messageData);
          }
        }

      } catch {
        // Ignore parsing errors for malformed JSON chunks
        console.warn('Failed to parse SSE message:', data);
      }
    }
  }
  return false; // Continue processing
}

export type UserInfo = {
  clientPrincipal: ClientPrincipal;
};

export type ClientPrincipal = {  
  claims: Claim[];
  identityProvider: string;
  userDetails: string;
  userId: string;
  userRoles: string[];  
}

export type Claim = {
  typ: string;
  val: string;
}

export async function getUserInfo(): Promise<UserInfo | undefined> {
  try {
    const response = await fetch("/.auth/me");
    if (!response.ok) {
      console.log(
        "No identity provider found. Access to chat will be blocked."
      );
      return undefined;
    }
    const payload = await response.json();
    //console.log(payload);
    return payload;
  } catch {
    return undefined;
  }
}

export async function getAuthStatus(emailAddress: string): Promise<CheckAuthResponse> {
  try {
    const response = await fetch("/api/checkauth", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-user-email-address": emailAddress
      },
    });
    if (!response.ok) {
      // In SWA, unauthenticated access can surface as 401/403 before the Function
      // executes. Treat this as "not signed in" so the UI redirects to login,
      // instead of showing a backend outage screen.
      if (response.status === 401 || response.status === 403) {
        return {
          backendConnectionFailed: false,
          isAuthEnforced: true,
          isSignedIn: false,
          hasAccessPermission: false,
          availableModels: [],
          availableIndexes: []
        };
      }
      throw new Error("Network response was not ok");
    }
    const config = await response.json(); // Parse JSON response
    return {
      isAuthEnforced: config.isAuthEnforced,
      hasAccessPermission: config.hasAccessPermission,
      isSignedIn: config.isSignedIn,
      backendConnectionFailed: false,
      userRole: config.userRole ?? undefined,
      userName: config.userName ?? undefined,
      availableModels: config.availableModels,
      availableIndexes: config.availableIndexes ?? []
    };
  } catch (error) {
    console.error("Failed to fetch configuration:", error);
     return {
      backendConnectionFailed: true,
      isAuthEnforced: false,
      isSignedIn: false,
      hasAccessPermission: false,
      availableModels: [],
      availableIndexes: []
    }; 
  }
}

export async function getAssistantTypeApi() {
  try {
    const response = await fetch("/api/assistanttype", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const config = await response.json(); // Parse JSON response
    return config;
  } catch (error) {
    console.error("Failed to fetch configuration:", error);
    return null; // Return null or some default value in case of error
  }
}

export const historyReadMessages = async (convId: string): Promise<ChatMessage[]> => {
  const response = await fetch("/api/history/read", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (res) => {
      if (!res) {
        return [];
      }
      const payload = await res.json();
      const messages: ChatMessage[] = [];
      if (payload?.messages) {
        payload.messages.forEach((msg: ChatMessage) => {
          const message: ChatMessage = {
            id: msg.id,
            role: msg.role,
            content: msg.content,            
            endTurn: msg.endTurn,
            date: msg.date,
            feedback: msg.feedback ?? undefined,
            context: msg.context,
            citations: msg.citations,
            proposedAnswers: msg.proposedAnswers,
            canBeResolved: msg.canBeResolved ?? false,
            model: msg.model
          };
          messages.push(message);
        });
      }
      return messages;
    })
    .catch(() => {
      console.error("There was an issue fetching your data.");
      return [];
    });
  return response;
};

export const historyReadFull = async (convId: string): Promise<ConversationHistory> => {

  const conversation: ConversationHistory = {
    conversation_id: convId,
    messages: [],
    read_only: true,
    title: ""   
  };

  const response = await fetch("/api/history/read", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (res) => {
      if (!res) {
        return conversation;
      }
      const payload = await res.json();
      
      conversation.title = payload.title;
      
      const messages: ChatMessage[] = [];
      if (payload?.messages) {
        payload.messages.forEach((msg: ChatMessage) => {
          const message: ChatMessage = {
            id: msg.id,
            role: msg.role,
            content: msg.content,            
            endTurn: msg.endTurn,
            date: msg.date,
            feedback: msg.feedback ?? undefined,
            context: msg.context,
            citations: msg.citations,
            proposedAnswers: msg.proposedAnswers,
            canBeResolved: msg.canBeResolved ?? false,
            model: msg.model
          };
          messages.push(message);
        });
      }

      conversation.messages = messages;

      return conversation;
    })
    .catch(() => {
      console.error("There was an issue fetching your data.");
      return conversation;
    });
  return response;
};

export const historyList = async (
  offset = 0
): Promise<Conversation[] | null> => {
  const response = await fetch(`/api/history/list?offset=${offset}`, {
    method: "GET",
  })
    .then(async (res) => {
      const payload = await res.json();
      if (!Array.isArray(payload)) {
        console.error("There was an issue fetching your data.");
        return null;
      }
      const conversations: Conversation[] = payload.map((conv: Conversation & { createdAt?: string; feedback?: string }) => {
        const conversation: Conversation = {
          id: conv.id,
          title: conv.title,
          date: conv.createdAt ?? conv.date,
          updatedAt: conv.updatedAt,
          messages: [],
          rating: (conv.feedback ?? conv.rating) as Conversation['rating'],
          additional_comments: conv.additional_comments
        };
        return conversation;
      });
      return conversations;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.", err);
      return null;
    });
  return response;
};

export const historySearch = async (
  query: string
): Promise<Conversation[] | null> => {
  try {
    const res = await fetch(`/api/history/search?query=${encodeURIComponent(query)}`, {
      method: "GET",
    });
    const payload = await res.json();
    if (!Array.isArray(payload)) {
      return null;
    }
    return payload.map((conv: Conversation & { createdAt?: string; feedback?: string }) => ({
      id: conv.id,
      title: conv.title,
      date: conv.createdAt ?? conv.date,
      updatedAt: conv.updatedAt,
      messages: [],
      rating: (conv.feedback ?? conv.rating) as Conversation['rating'],
      additional_comments: conv.additional_comments
    }));
  } catch (err) {
    console.error("Failed to search conversations:", err);
    return null;
  }
};

export const historyRename = async (
  convId: string,
  title: string
): Promise<Response> => {
  const response = await fetch("/api/history/rename", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      title: title,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((res) => {
      return res;
    })
    .catch(() => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyDelete = async (convId: string): Promise<Response> => {
  const response = await fetch("/api/history/delete", {
    method: "DELETE",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((res) => {
      return res;
    })
    .catch(() => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyDeleteAll = async (): Promise<Response> => {
  const response = await fetch("api/history/delete_all", {
    method: "DELETE",
    body: JSON.stringify({}),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((res) => {
      return res;
    })
    .catch(() => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyGiveFeedback = async (  
  convId: string,
  messageId: string,
  feedback: Feedback
): Promise<Response> => {
  const response = await fetch("/api/history/feedback", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      chat_message_id: messageId,
      feedback: feedback
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (res) => {
      return res;
    })
    .catch(() => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyGiveConversationFeedback = async (
  conversationId: string,
  rating: ConversationRating,
  comment?: string
): Promise<Response> => {
  const response = await fetch("/api/history/feedback-conversation", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: conversationId,
      feedback: rating,
      additional_comments: comment
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (res) => {
      return res;
    })
    .catch(() => {
      console.error("There was an issue fetching your data.");
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export async function getFrontEndSettings(): Promise<FrontEndSettings> {
  try {
    const response = await fetch("/api/history/frontend_settings", {
      method: "GET",
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const responseJSON = await response.json();
    return responseJSON
  } catch (error) {
    console.error("Failed to fetch Front End Settings:", error);
    return { CHAT_HISTORY_ENABLED: false };
  }
}

export const requestResolutionCode = async (convId: string): Promise<ChatResponse> => {
  const response = await fetch("/api/resolution-code", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error("Failed to request resolution code");
      }
      const payload = await res.json();

      console.log(payload);

      const chatResponse: ChatResponse = {
        conversationId: payload.conversationId,
        date: payload.date,
        title: payload.title,
        message: payload.message ? {
          id: payload.message.id,
          role: payload.message.role,
          content: payload.message.content,
          endTurn: payload.message.endTurn,
          date: payload.message.date,
          feedback: payload.message.feedback ?? undefined,
          context: payload.message.context,
          citations: payload.message.citations,
          proposedAnswers: payload.message.proposedAnswers,
          canBeResolved: payload.message.canBeResolved ?? false
        } : undefined,
        error: payload.error
      };
      return chatResponse;
    })
    .catch((err) => {
      console.error("There was an issue requesting resolution code.", err);
      throw err;
    });
  return response;
}

export async function getPromptTemplates(): Promise<{ templates: Record<string, unknown>[], canManagePrompts: boolean }> {
  try {
    const response = await fetch("/api/prompt-templates", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      // Check if it's a 404 - backend doesn't support this feature yet
      if (response.status === 404) {
        console.warn("[Prompt Templates] Backend does not support prompt-templates functionality. Update backend to latest version with prompt-templates support.");
        return { templates: [], canManagePrompts: false };
      }
      throw new Error("Failed to fetch prompt templates");
    }

    const data = await response.json() as { templates?: Record<string, unknown>[], canManagePrompts?: boolean };
    return {
      templates: data.templates || [],
      canManagePrompts: data.canManagePrompts || false
    };
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      console.warn("[Prompt Templates] Unable to connect to backend. Prompt templates functionality disabled.");
    } else {
      console.error("[Prompt Templates] Error fetching prompt templates:", error);
    }
    return { templates: [], canManagePrompts: false };
  }
}

export async function createPromptTemplate(template: {
  promptKey: string;
  name: string;
  description: string;
  content: string;
  category: string;
  tags?: string[];
}): Promise<Record<string, unknown>> {
  const response = await fetch("/api/prompt-templates", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(template),
  });

  if (!response.ok) {
    if (response.status === 404) {
      console.warn("[Prompt Templates] Backend does not support prompt-templates functionality. Update backend to latest version.");
      throw new Error("Prompt templates feature not available in current backend version");
    }
    const error = await response.json();
    throw new Error(error.error || "Failed to create prompt template");
  }

  return await response.json();
}

export async function updatePromptTemplate(template: {
  promptTemplateId: string;
  promptKey: string;
  name: string;
  description: string;
  content: string;
  category: string;
  tags?: string[];
}): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetch(`/api/prompt-templates/${template.promptTemplateId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(template),
    });
  } catch (networkError) {
    throw new Error("Network error: cannot reach the backend. Is the backend running?");
  }

  if (!response.ok) {
    if (response.status === 404) {
      console.warn("[Prompt Templates] Backend does not support prompt-templates functionality. Update backend to latest version.");
      throw new Error("Prompt templates feature not available in current backend version");
    }
    let errorMessage = `HTTP ${response.status}`;
    try {
      const error = await response.json();
      errorMessage = error.error || errorMessage;
    } catch {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

export async function deletePromptTemplate(templateId: string): Promise<void> {
  const response = await fetch(`/api/prompt-templates/${templateId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    if (response.status === 404) {
      console.warn("[Prompt Templates] Backend does not support prompt-templates functionality. Update backend to latest version.");
      throw new Error("Prompt templates feature not available in current backend version");
    }
    const error = await response.json();
    throw new Error(error.error || "Failed to delete prompt template");
  }
}

// --- Prompt Template Governance API Functions ---

/** Validates that a string is a well-formed UUID to prevent path traversal in API routes. */
function assertValidUuid(value: string, name: string): void {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(value)) {
    throw new Error(`Invalid ${name}: must be a valid UUID.`);
  }
}

/** The set of version statuses accepted by the governance API. */
export type VersionStatus = 'Approved' | 'Deprecated' | 'Reactivate';

export async function getTemplateVersions(templateId: string): Promise<PromptTemplateVersion[]> {
  assertValidUuid(templateId, 'templateId');
  const response = await fetch(`/api/prompt-templates/${templateId}/versions`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Template '${templateId}' not found (404).`);
    }
    const error = await response.json();
    throw new Error(error.error || "Failed to fetch template versions");
  }

  return await response.json();
}

export async function createTemplateVersion(
  templateId: string,
  content: string,
  changeDescription?: string
): Promise<PromptTemplateVersion> {
  assertValidUuid(templateId, 'templateId');
  if (!content || !content.trim()) {
    throw new Error("Content must not be empty.");
  }
  if (content.length > 50000) {
    throw new Error("Content must not exceed 50,000 characters.");
  }
  if (changeDescription && changeDescription.length > 2000) {
    throw new Error("Change description must not exceed 2,000 characters.");
  }

  // Sanitize user-supplied text to prevent script injection before sending to backend.
  const sanitize = (s: string) => s.replace(/[<>]/g, '');
  const sanitizedContent = sanitize(content);
  const sanitizedDescription = changeDescription ? sanitize(changeDescription) : undefined;

  const response = await fetch(`/api/prompt-templates/${templateId}/versions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      promptTemplateId: templateId,
      content: sanitizedContent,
      changeDescription: sanitizedDescription,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to create template version");
  }

  return await response.json();
}

export async function updateVersionStatus(
  versionId: string,
  status: VersionStatus,
  reason?: string
): Promise<Record<string, unknown> | undefined> {
  assertValidUuid(versionId, 'versionId');
  const response = await fetch(`/api/prompt-templates/versions/${versionId}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      promptTemplateVersionId: versionId,
      status,
      reason,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to update version status");
  }

  return response.json().catch(() => undefined);
}

export async function uploadConversationFile(conversationId: string, file: File): Promise<ConversationAttachment> {
  const formData = new FormData();
  formData.append("conversationId", conversationId);
  formData.append("file", file);

  const response = await fetch("/api/conversation-upload", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    const err = new Error(error.error || "Failed to upload file");
    if (error.details) {
      (err as any).details = error.details;
    }
    throw err;
  }

  return await response.json();
}

export async function deleteConversationFile(attachmentId: string): Promise<void> {
  const response = await fetch(`/api/conversation-upload/${attachmentId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error("Failed to delete attachment");
  }
}

export async function getConversationAttachments(conversationId: string): Promise<ConversationAttachment[]> {
  const response = await fetch(`/api/conversation-upload/${conversationId}`, {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error("Failed to get attachments");
  }

  return await response.json();
}

export async function exportConversation(request: ExportRequest): Promise<ExportResponse> {
  const response = await fetch("/api/export", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  let data: ExportResponse;
  try {
    data = await response.json();
  } catch {
    return {
      error: `Export failed with status ${response.status}. The server response could not be parsed.`,
    };
  }

  if (!response.ok) {
    return {
      error: data.error || `Export failed (HTTP ${response.status}). Please try again.`,
    };
  }

  return data;
}

export async function exportAllConversations(request: ExportAllRequest): Promise<ExportResponse> {
  const response = await fetch("/api/export", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  let data: ExportResponse;
  try {
    data = await response.json();
  } catch {
    return { error: `Export failed with status ${response.status}. The server response could not be parsed.` };
  }

  if (!response.ok) {
    return { error: data.error || `Export failed (HTTP ${response.status}). Please try again.` };
  }

  return data;
}

// ─── Share API ───────────────────────────────────────────────────────────────

export interface ShareConversationResponse {
  share_id: string;
  share_token: string;
  share_url: string;
  created_at: string;
}

export interface ConversationShareInfo {
  share_id: string;
  conversation_id: string;
  share_token: string;
  share_url: string;
  created_at: string;
  is_revoked: boolean;
  revoked_at: string | null;
}

export const historyShare = async (convId: string): Promise<ShareConversationResponse | null> => {
  try {
    const response = await fetch("/api/history/share", {
      method: "POST",
      body: JSON.stringify({ conversation_id: convId }),
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error("Failed to share conversation:", err);
    return null;
  }
};

export const historyGetShare = async (convId: string): Promise<ConversationShareInfo | null> => {
  try {
    const response = await fetch(`/api/history/shares?conversation_id=${encodeURIComponent(convId)}`, {
      method: "GET",
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data ?? null;
  } catch (err) {
    console.error("Failed to fetch share:", err);
    return null;
  }
};

export const historyRevokeShare = async (convId: string): Promise<boolean> => {
  try {
    const response = await fetch("/api/history/share/revoke", {
      method: "POST",
      body: JSON.stringify({ conversation_id: convId }),
      headers: { "Content-Type": "application/json" },
    });
    return response.ok;
  } catch (err) {
    console.error("Failed to revoke share:", err);
    return false;
  }
};

export const historyReadShared = async (shareToken: string): Promise<ConversationHistory> => {
  const conversation: ConversationHistory = {
    conversation_id: "",
    messages: [],
    read_only: true,
    title: "",
  };

  try {
    const response = await fetch(`/api/history/shared/${encodeURIComponent(shareToken)}`, {
      method: "GET",
    });
    if (!response.ok) return conversation;
    const payload = await response.json();
    conversation.conversation_id = payload.conversation_id;
    conversation.title = payload.title;
    conversation.messages = (payload.messages || []).map((msg: ChatMessage) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      endTurn: msg.endTurn,
      date: msg.date,
      feedback: msg.feedback ?? undefined,
      context: msg.context,
      citations: msg.citations,
      proposedAnswers: msg.proposedAnswers,
      canBeResolved: msg.canBeResolved ?? false,
    }));
    return conversation;
  } catch {
    console.error("There was an issue fetching the shared conversation.");
    return conversation;
  }
};

// ─── Agent API ───────────────────────────────────────────────────────────────

export interface AgentApiDto {
  agentId: string;
  name: string;
  description: string | null;
  iconName: string;
  route: string;
  remoteUrl: string | null;
  remoteScope: string | null;
  remoteModule: string | null;
  isActive: boolean;
  sortOrder: number;
  hasAccess: boolean;
}

export const fetchAgents = async (): Promise<AgentApiDto[]> => {
  const response = await fetch("/api/agents", {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch agents: ${response.status}`);
  const data = await response.json();
  return data.agents;
};

// ---- Admin API ----

export interface AdminUser {
  userId: string;
  email: string | null;
  name: string | null;
  role: string;
  lastLogin: string | null;
  agentAccessCount: number;
  /** GUIDs of all tenants this user is assigned to. Empty = unrestricted (internal) user. */
  tenantIds: string[];
  /** Display names of the assigned tenants. Parallel array to tenantIds. */
  tenantNames: string[];
}

export interface UserAgentAccess {
  agentId: string;
  agentName: string | null;
  grantedAt: string;
  grantedBy: string | null;
}

export const fetchAdminUsers = async (): Promise<AdminUser[]> => {
  const response = await fetch("/api/useradmin/users", {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
  const data = await response.json();
  return data.users;
};

export const updateUserRole = async (userId: string, role: string): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to update role: ${response.status}`);
  }
};

export const fetchUserAgentAccess = async (userId: string): Promise<UserAgentAccess[]> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/agents`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch agent access: ${response.status}`);
  const data = await response.json();
  return data.accesses;
};

export const grantAgentAccess = async (userId: string, agentIds: string[]): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/agents`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ agentIds }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to grant access: ${response.status}`);
  }
};

export const revokeAgentAccess = async (userId: string, agentId: string): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/agents/${encodeURIComponent(agentId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to revoke access: ${response.status}`);
  }
};

export const grantAllAgentAccess = async (userId: string): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/agents/grant-all`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to grant all access: ${response.status}`);
  }
};

export const revokeAllAgentAccess = async (userId: string): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/agents`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to revoke all access: ${response.status}`);
  }
};

export const addUser = async (email: string, name: string | null, role: string): Promise<void> => {
  const response = await fetch("/api/useradmin/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, name, role }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to add user: ${response.status}`);
  }
};

export const deleteUser = async (userId: string): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to delete user: ${response.status}`);
  }
};

// ── Tenant Admin API ─────────────────────────────────────────────────────────────────────

export interface TenantDto {
  tenantId: string;
  name: string;
  /** Lowercase identifier that matches the customerId field stamped on indexed documents. Immutable after creation. */
  externalIdentifier: string;
  allowedIndexIds: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Number of users currently assigned to this tenant. */
  assignedUserCount: number;
}

export const fetchTenants = async (): Promise<TenantDto[]> => {
  const response = await fetch("/api/useradmin/tenants", { method: "GET" });
  if (!response.ok) throw new Error(`Failed to fetch tenants: ${response.status}`);
  const data = await response.json();
  return data.tenants ?? [];
};

export const createTenant = async (body: {
  name: string;
  externalIdentifier: string;
  allowedIndexIds: string[];
  isActive: boolean;
}): Promise<string> => {
  const response = await fetch("/api/useradmin/tenants", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to create tenant: ${response.status}`);
  }
  const data = await response.json();
  return data.tenantId as string;
};

/** externalIdentifier is intentionally excluded — it is immutable after creation. */
export const updateTenant = async (
  tenantId: string,
  body: { name?: string; allowedIndexIds?: string[]; isActive?: boolean }
): Promise<void> => {
  const response = await fetch(`/api/useradmin/tenants/${encodeURIComponent(tenantId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to update tenant: ${response.status}`);
  }
};

export const deleteTenant = async (tenantId: string): Promise<void> => {
  const response = await fetch(`/api/useradmin/tenants/${encodeURIComponent(tenantId)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to delete tenant: ${response.status}`);
  }
};

/** Pass an empty array to remove all tenant assignments (user becomes unrestricted). */
export const assignUserToTenant = async (userId: string, tenantIds: string[]): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/tenant`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenantIds }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to assign tenant: ${response.status}`);
  }
};

export interface SearchIndexSummaryDto {
  id: string;
  displayName: string;
  category: string;
}

export const fetchSearchIndexes = async (): Promise<SearchIndexSummaryDto[]> => {
  const response = await fetch("/api/useradmin/search-indexes", { method: "GET" });
  if (!response.ok) throw new Error(`Failed to fetch search indexes: ${response.status}`);
  const data = await response.json();
  return data.indexes ?? [];
};

// ─── Space Access Admin API ──────────────────────────────────────────────────

/** Represents a single space available for user access assignment. */
export interface SpaceDto {
  spaceId: string;
  spaceName: string;
}

/** Spaces grouped by category for UI display. */
export interface GroupedSpacesDto {
  category: string;
  spaces: SpaceDto[];
}

export interface UserSpaceAccessDto {
  spaceId: string;
  spaceName: string;
  category: string;
}

/** Fetch all available spaces grouped by category. */
export const fetchAllSpacesGrouped = async (): Promise<GroupedSpacesDto[]> => {
  const response = await fetch("/api/useradmin/spaces", {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch spaces: ${response.status}`);
  return response.json();
};

/** Fetch spaces assigned to a specific user. */
export const fetchUserSpaceAccess = async (userId: string): Promise<UserSpaceAccessDto[]> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/spaces`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch user spaces: ${response.status}`);
  const data = await response.json();
  return data.spaces ?? [];
};

/** Assign spaces to a user (replaces all existing assignments). */
export const assignUserToSpaces = async (userId: string, spaceIds: string[]): Promise<void> => {
  const response = await fetch(`/api/useradmin/users/${encodeURIComponent(userId)}/spaces`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ spaceIds }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to assign spaces: ${response.status}`);
  }
};

// Type aliases for convenience
export type UserDto = AdminUser;
export const fetchAllUsers = fetchAdminUsers;
export const fetchAllSpaces = fetchAllSpacesGrouped;

// ─── Analytics API ───────────────────────────────────────────────────────────

export interface TimeSeriesDataPoint {
  period: string;
  value: number;
  averageValue?: number;
}

export interface ModelUsage {
  model: string;
  count: number;
  percentage: number;
}

export interface ProductUsage {
  indexId: string;
  displayName: string;
  count: number;
  percentage: number;
}

export interface TopUser {
  userId: string;
  userName: string;
  email: string;
  queryCount: number;
  lastActive: string | null;
}

export interface SentimentSummary {
  positive: number;
  negative: number;
  noFeedback: number;
  sentimentOverTime: TimeSeriesDataPoint[];
}

export interface DashboardSummary {
  totalQueries: number;
  uniqueUsers: number;
  activeUsersNow: number;
  averageResponseTimeSec: number | null;
  queryVolume: TimeSeriesDataPoint[];
  uniqueUsersOverTime: TimeSeriesDataPoint[];
  responseTimeOverTime: TimeSeriesDataPoint[];
  modelUsage: ModelUsage[];
  productUsage: ProductUsage[];
  sentiment: SentimentSummary;
  topUsers: TopUser[];
}

export interface ActiveUsers {
  count: number;
  timestamp: string;
}

export interface CostTimeSeriesPoint {
  period: string;
  cost: number;
}

export interface CostByService {
  serviceName: string;
  cost: number;
  percentage: number;
}

export interface AzureCostSummary {
  totalCost: number;
  currency: string;
  costOverTime: CostTimeSeriesPoint[];
  costByService: CostByService[];
}

export const fetchDashboardSummary = async (
  from: string,
  to: string,
  granularity: string,
  top: number = 50
): Promise<DashboardSummary> => {
  const params = new URLSearchParams({ from, to, granularity, top: String(top) });
  const response = await fetch(`/api/analytics/dashboard?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to fetch dashboard: ${response.status}`);
  }
  return response.json();
};

export const fetchActiveUsers = async (): Promise<ActiveUsers> => {
  const response = await fetch("/api/analytics/active-users", {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to fetch active users: ${response.status}`);
  }
  return response.json();
};

export const fetchAzureCosts = async (
  from: string,
  to: string,
  granularity: string
): Promise<AzureCostSummary> => {
  const params = new URLSearchParams({ from, to, granularity });
  const response = await fetch(`/api/analytics/costs?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to fetch Azure costs: ${response.status}`);
  }
  return response.json();
};
